import React from 'react';
import Styled from 'styled';
import PropTypes from 'prop-types';
import Helmet from 'helmet/helmet';
import Route from 'react-router/route';
import { FormattedMessage } from 'react-intl';

import { RouteWithSearchRenderProps } from 'lib/react-router/route-with-search';
import observe from 'lib/intersection-observer/observe';
import ScrollToTop from 'lib/scroll/scroll-to-top';
import FormattedDateAgoView from 'lib/intl/formatted-date-ago-view';

import FetchMoreButton, { adjacentHrefsFromCursor } from 'four-nets/ui-v2/fetch-more-button/fetch-more-button';
import { Site } from 'four-nets/site';
import FeedImages from 'four-nets/photoblog/posts/common/feed-images/feed-images';
import Footer from 'four-nets/photoblog/posts/common/footer/footer';
import Comments from 'four-nets/photoblog/posts/common/comments/comments';
import { AlbumFragment } from 'four-nets/photoblog/posts/album/album.graphql';
import UserBrands from 'four-nets/user-brand/user-brands/user-brands';
import isPrArticleUsername from 'four-nets/common/is-pr-article-username';

import { AlbumWeddingFragment } from '../album-query.graphql';
import RecommendedCollections from './recommended-collections/recommended-collections';
import NiceImageGrid from './nice-image-grid/nice-image-grid';
import Description from './description/description';
import DescriptionContent from './description-content/description-content';
import AuthorTop from './author-top/author-top';
import AuthorBottom from './author-bottom/author-bottom';
import AuthorAlbums from './author-albums/author-albums';
import defaultStyles from './detail.scss';
import { CommentOrderingFilterEnum } from '../../common/comments/comments.graphql';

const INTERSECTION_OPTIONS = { rootMargin: '2000px' };

export interface Props extends AlbumFragment, RouteWithSearchRenderProps {
    styles: typeof defaultStyles;
    fetchMore?: (all?: boolean) => Promise<any>;
    refetch: () => any;
    commentId?: string;
    pathname: string;
}

interface State {
    loading?: boolean;
    visible: boolean;
}

class Detail extends React.PureComponent<Props, State> {
    static contextTypes = {
        origin: PropTypes.string,
        site: PropTypes.instanceOf(Site),
    };

    unobserve?: () => void;
    ref: React.RefObject<HTMLDivElement>;

    constructor(props: Props) {
        super(props);
        this.ref = React.createRef();
        this.state = { visible: false };
    }

    componentDidMount() {
        const element = this.ref && (this.ref.current as Element | null);
        if (element) {
            this.unobserve = observe(
                element,
                () => {
                    this.setState({ visible: true });
                },
                INTERSECTION_OPTIONS
            );
        }
    }

    componentWillUnmount() {
        if (this.unobserve) {
            this.unobserve();
        }
    }

    initObserveElement = (force: boolean = false) => {
        const element = this.ref && (this.ref.current as Element | null);
        if (element && (!this.unobserve || force)) {
            this.setState({ visible: false }, () => {
                this.unobserve = observe(
                    element,
                    () => {
                        this.setState({ visible: true });
                    },
                    INTERSECTION_OPTIONS
                );
            });
        }
    };

    componentDidUpdate(_: Props, prevState: State) {
        this.initObserveElement();

        /* When the fetchMore button appeared, we want to fetchMore if autoload is still active */
        const appeared = this.state.visible && !prevState.visible;
        if (appeared && !this.state.loading && this.props.fetchMore) {
            this.fetchMore();
            this.setState({ visible: false });
        }
    }

    fetchMore = () => {
        if (this.props.fetchMore) {
            this.setState({ loading: true });
            return this.props.fetchMore(false).finally(() => {
                this.setState({ loading: false });
            });
        }
        return Promise.resolve();
    };

    renderHelmet = (H: any) => {
        const { origin, site } = this.context;
        const { title } = this.props;

        return (
            <Route
                render={({ location }) => (
                    <H>
                        <title>{title ? `${title} | ${site.name}` : `${site.name}`}</title>
                        <meta property='og:title' content={title as string} />
                        <meta property='og:url' content={`${origin}/${location.pathname}`} />
                    </H>
                )}
            />
        );
    };

    imageIdToHref = (imageId: string) => {
        const { user, slug } = this.props;
        if (user!.profile!.isVendor) {
            return `/catalog/vendor/${user && user.username}/blog-album/${slug}/${imageId}/`;
        }
        return `/blog/${user && user.username}/album/${slug}/${imageId}/`;
    };

    render() {
        const { styles, refetch, fetchMore, commentId, pathname, ...album } = this.props;

        const [endCursorHref, prevCursorHref] = adjacentHrefsFromCursor({
            endCursor: album.images && album.images.pageInfo.endCursor,
            pathname,
            pageSize: 100,
        });

        return (
            <div className={styles.pageWrapper}>
                <Helmet render={this.renderHelmet} />
                {commentId ? null : <ScrollToTop />}
                <div className={styles.topWrapper}>
                    <div>
                        <Description {...album} />
                        {album.isWeddingAlbum ? (
                            <div className={styles.weddingAlbumDescription}>
                                <FormattedMessage id='HOW_WAS_THE_WEDDING' />?
                                {album.user &&
                                album.user.photoBlog &&
                                (album.user.photoBlog.details as unknown as AlbumWeddingFragment) ? (
                                    <div className={styles.weddingInfo}>
                                        {
                                            <FormattedDateAgoView
                                                value={
                                                    new Date(
                                                        (
                                                            album.user.photoBlog
                                                                .details as unknown as AlbumWeddingFragment
                                                        ).weddingDate
                                                    )
                                                }
                                            />
                                        }{' '}
                                        |{' '}
                                        {(album.user.photoBlog.details as unknown as AlbumWeddingFragment).weddingCity}{' '}
                                        |{' '}
                                        {(album.user.photoBlog.details as unknown as AlbumWeddingFragment).county &&
                                            (album.user.photoBlog.details as unknown as AlbumWeddingFragment).county!
                                                .name}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        <DescriptionContent
                            isVendor={album.user?.profile.vendor?.isPresentationPaid}
                            isPrArticle={isPrArticleUsername(album.user?.username || '')}
                            content={album.content || ''}
                        />
                        {album.user && album.user.username && album.isWeddingAlbum ? (
                            <UserBrands username={album.user.username} />
                        ) : null}
                    </div>
                    <div className={styles.actions}>
                        <AuthorTop
                            style={{ editLink: styles.authorTopEditLink, wrapper: styles.authorTopWrapper }}
                            {...album}
                        />
                    </div>
                </div>
                <React.Fragment>
                    <div className={styles.feedWrapper}>
                        <FeedImages {...album} fetchMore={fetchMore} fetchMoreIndex={album.imagesCount || undefined}>
                            {(imgRef, onClick) => (
                                <NiceImageGrid
                                    style={{ grid: styles.grid }}
                                    images={album && album.images}
                                    imgRef={imgRef as any}
                                    onClick={onClick as any}
                                    imageIdToHref={this.imageIdToHref}
                                    showStatsOnHover={true}
                                />
                            )}
                        </FeedImages>
                    </div>
                    {fetchMore ? (
                        <div ref={this.ref}>
                            <FetchMoreButton
                                disabled={this.state.loading}
                                loading={this.state.loading}
                                onClick={() => {
                                    this.fetchMore();
                                    this.initObserveElement(true);
                                }}
                                href={endCursorHref}
                                prevHref={prevCursorHref}
                            />
                        </div>
                    ) : null}
                    <div className={styles.bottomRow}>
                        <div className={styles.comments}>
                            <Footer refetch={refetch} {...album} commentsOpen={true} inFeed={true} />
                            {album.id ? (
                                <Comments
                                    commentId={commentId}
                                    variables={{
                                        parentId: album.id,
                                    }}
                                />
                            ) : null}
                        </div>
                        <div>
                            <AuthorBottom {...album} />
                            {album.user ? <AuthorAlbums user={album.user} albumSlug={album.slug} /> : null}
                            <RecommendedCollections />
                        </div>
                    </div>
                </React.Fragment>
            </div>
        );
    }
}

export default Styled(defaultStyles)(Detail);
